import { coinIcon } from "@/utils/coin-icon";
import { useEffect } from "react";
import { useTranslations } from "use-intl";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/store";
import {
  getPortfolioIncomeHistory$,
  getPortfolioSummary$,
  setStatisticsCurrency,
} from "@/store/sub_accounts";
import { Card } from "@/components/Card";
import { StatCard } from "@/components/StatCard";
import { IncomeByCoinChart } from "@/components/IncomeByCoinChart";
import { formatHash } from "@/utils/to-hash";
import { Spinner } from "react-bootstrap";
import style from "./index.module.scss";

// null: no Qoldau rate for the coin / day - shown as unknown, never as 0.
const formatKzt = (value: number | null) =>
  value === null ? "—" : `${Math.round(value).toLocaleString("ru-RU")} ₸`;

// Screen 1 ("Все активы"): rollup across every currency, from the new
// GET /api/v1/pools/sub_account/portfolio_summary/ endpoint. Amounts are
// KZT-only per product decision - no native crypto amounts, no $ sub-value.
export function PortfolioOverview() {
  const i18n = useTranslations("cabinet.statistics");
  const dispatch = useAppDispatch();
  const summary = useSelector(
    (state: RootState) => state.subAccount.portfolioSummary,
  );
  const failed = useSelector(
    (state: RootState) => state.subAccount.portfolioSummaryFailed,
  );
  const incomeHistory = useSelector(
    (state: RootState) => state.subAccount.portfolioIncomeHistory,
  );

  useEffect(() => {
    const summaryPromise = dispatch(getPortfolioSummary$());
    const historyPromise = dispatch(getPortfolioIncomeHistory$());
    return () => {
      summaryPromise.abort();
      historyPromise.abort();
    };
  }, []);

  if (!summary) {
    return failed ? (
      <p className={style.note}>{i18n("portfolio.loadFailed")}</p>
    ) : (
      <Spinner animation="grow" />
    );
  }
  const missingKzt = summary.totals.kzt_missing_currencies || [];

  const { totals, by_currency } = summary;

  return (
    <div>
      <div className={style.kpiGrid}>
        <Card>
          <StatCard
            color="blue"
            icon="fa-dollar-sign"
            label={i18n("income24h")}
            value={formatKzt(totals.income_24h_kzt)}
          />
        </Card>
        <Card>
          <StatCard
            color="teal"
            icon="fa-wallet"
            label={i18n("portfolio.totalBalance")}
            value={formatKzt(totals.balance_kzt)}
          />
        </Card>
        <Card>
          <StatCard
            color="purple"
            icon="fa-layer-group"
            label={i18n("portfolio.subAccounts")}
            value={String(totals.sub_accounts_count)}
          />
        </Card>
        <Card>
          <StatCard
            color="orange"
            icon="fa-users"
            label={i18n("worker")}
            value={String(totals.workers_online + totals.workers_offline)}
            subValue={`${totals.workers_online} online`}
          />
        </Card>
        <Card>
          <StatCard
            color="green"
            icon="fa-coins"
            label={i18n("portfolio.activeCurrencies")}
            value={String(totals.currencies.length)}
            subValue={totals.currencies.join(", ")}
          />
        </Card>
      </div>
      <Card className={style.tableCard}>
        <h5 className={style.tableTitle}>{i18n("portfolio.myCoins")}</h5>
        <div className="generic-table">
          <table className="table">
            <thead>
              <tr>
                <th>{i18n("portfolio.coin")}</th>
                <th>{i18n("portfolio.subAccounts")}</th>
                <th>{i18n("aggregateTable.hashrateNow")}</th>
                <th>{i18n("aggregateTable.online")}</th>
                <th>{i18n("income24h")}</th>
                <th>{i18n("balance")}</th>
              </tr>
            </thead>
            <tbody>
              {by_currency.map((row) => (
                <tr
                  key={row.currency}
                  className={style.row}
                  onClick={() => dispatch(setStatisticsCurrency(row.currency))}
                >
                  <td>
                    {coinIcon(row.currency) && (
                      <img
                      className={style.coinIcon}
                      src={coinIcon(row.currency)}
                      alt={row.currency}
                    />
                    )}
                    {row.currency}
                  </td>
                  <td>{row.sub_accounts_count}</td>
                  <td>{formatHash(row.hashrate_10m, row.currency)}</td>
                  <td>{row.workers_online}</td>
                  <td>{formatKzt(row.income_24h_kzt)}</td>
                  <td>{formatKzt(row.balance_kzt)}</td>
                </tr>
              ))}
              {!by_currency.length && (
                <tr>
                  <td colSpan={6} className="text-center">
                    —
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {incomeHistory && incomeHistory.series.length > 0 && (
        <Card className={style.chartCard}>
          <h5>{i18n("portfolio.incomeChartTitle")}</h5>
          <IncomeByCoinChart history={incomeHistory} />
        </Card>
      )}
      {missingKzt.length > 0 && (
        <p className={style.note}>
          {i18n("portfolio.noRate", { coins: missingKzt.join(", ") })}
        </p>
      )}
      <p className={style.note}>{i18n("portfolio.note")}</p>
    </div>
  );
}
